import { FieldSettingsModel } from '@syncfusion/ej2-angular-dropdowns'
import { Reference } from '@shared/layer-property/layer-property.component'
import { CreditModelScenarioMapping, CreditPortfolio } from './credit-portfolio.model'
import {
  CreditTransaction,
  CreditTranche,
  CreditCalculationStructure,
} from './credit-structure.model'
import { CreditTransactionType } from './credit-layer.model'
import { CreditTriggers } from './credit-trigger.model'
import { CreditSubmissionUserTranche } from './credit-submission.model'
import { MetricValueType } from '../../core/model/metric-value-type.model'
import { sortModelScenarioMapping } from '../utils/credit.utils'

export type CreditProperty = {
  id?: string
  key: CreditPropertyKey
  label: string
  lens: CreditPropertyLens
  type: CreditPropertyType
  references?: CreditPropertyReference[]
  fields?: FieldSettingsModel
  readonly?: boolean
  value?: CreditPropertyValue
  dataSource?: CreditDropdownOption[] | any[]
  dataSourceSort?: (a: any, b: any) => number
  clampMax?: number | null
}

export type CreditPropertyKey =
  | keyof CreditPortfolio
  | keyof CreditCalculationStructure
  | keyof CreditTransaction
  | keyof CreditTranche
  | keyof CreditSubmissionUserTranche
  | PanelProps

export type PanelProps =
  | 'color'
  | 'tranches'
  | 'pricingCurve'
  | 'term'
  | 'businessCovered'
  | 'expectedPremium'
  | 'reinsurerParticipationLimit'
  | 'dollarRetention'
  | 'dollarLimit'
  | 'retention_type'
  | 'reference_tranche.min_ce_target'
  | 'reference_tranche.delinquency_threshold_pct'
  | 'reference_tranche.distressed_buckets'
  | 'reference_tranche.cnl_gates_subordinate'
  | 'reference_tranche.a1_cnl_threshold'
  | 'reference_tranche.supplemental_target_pct'
  | 'reference_tranche.first_payment_date'
  | 'reference_tranche.cumulative_net_loss_schedule'

export type CreditPropertyLens =
  | 'portfolio'
  | 'structure'
  | 'transaction'
  | 'tranche'
  | 'transaction&tranche'

export type CreditPropertyType =
  | MetricValueType
  | 'layer-type'
  | 'popup'
  | 'switch'
  | 'text-upper'
  | 'color-picker'
  | 'stepdown-trigger'
  | 'date-range'
  | 'dropdown'
  | 'dropdown-tree'

export type CreditPropertyReference = Reference

export interface CreditDropdownOption {
  value: number
  text: string
}

export const CEDING_COMMISSION_OPTIONS: CreditDropdownOption[] = [
  { value: 0.3, text: '30%' },
  { value: 0.35, text: '35%' },
  { value: 0.4, text: '40%' },
]

export const CEDING_COMMISSION_FIELDS = { text: 'text', value: 'value' } as const

export const CALL_TYPE_SELECTOR_TYPES: CreditPropertyReference[] = [
  { value: 'economic', viewValue: 'Economic' },
  { value: 'earliest', viewValue: 'Earliest' },
  { value: 'never', viewValue: 'Never' },
] as CreditPropertyReference[]

export type CreditPropertyValue =
  | number
  | string
  | CreditTriggers
  | CreditTranche[]
  | string[]
  | CreditModelScenarioMapping

export enum PropertyLabel {
  TransactionType = 'Transaction Type',
  Name = 'Name', // for qs needs to be set at both transaction & tranche
  EffectiveDate = 'Effective Date',
  MaturityDate = 'Maturity Date',
  CallDate = 'Call Date',
  AcquisitionDate = 'Acquisition Date',
  CallType = 'Call Type',
  LimitStepdownType = 'Limit Stepdown Type',
  RetentionType = 'Retention Type',
  StepDownTrigger = 'Step Down Trigger',
  CleanUpCall = 'Clean Up Call %',
  Cession = 'Cession %', // for qs be set at both transaction & tranche
  AnnualLrCap = 'Annual LR Cap',
  AggregateLrCap = 'Aggregate LR Cap',
  ProfitCommission = 'Profit Commission',
  CedingCommission = 'Ceding Commission',
  Wacl = 'WACL',
  Wahc = 'WAHC',
  Tranches = 'Tranches',
  PricingCurve = 'Pricing Curve',
  Color = 'Color', // part of tranche metadata
  Term = 'Term', // maturity date - effective date (in years)
  CollateralRequirement = 'Collateral Requirement',
  Attach = 'Attach %',
  Detach = 'Detach %',
  ROL = 'ROL',
  BusinessCovered = 'Business Covered',
  ExpectedPremium = 'Expected Premium $',
  DollarRetention = '$ Retention',
  DollarLimit = '$ Limit',
  ReinsurerParticipationLimit = 'Reinsurer Participation $',
  ReinsurerParticipationPercent = 'Reinsurer Participation %',
  PaymentFrequency = 'Payment Frequency',
  FixedCost = 'Fixed Cost',
  RORACScenario = 'RORAC Scenario',
  Margin = 'Margin',
  MinCeTarget = 'Minimum CE Target',
  DelinquencyThreshold = 'Delinquency Threshold',
  DistressedBuckets = 'Distressed Buckets',
  CnlGatesSubordinate = 'CNL Gates Subordinate',
  A1CnlThreshold = 'A-1 CNL Threshold',
  SupplementalTarget = 'Supplemental Target',
  FirstPaymentDate = 'First Payment Date',
  CumulativeNetLoss = 'Cumulative Net Loss',
}

/* Properties Panel */
const SHARED_PROPERTIES: CreditProperty[] = [
  {
    key: 'transaction_type',
    label: PropertyLabel.TransactionType,
    lens: 'transaction',
    type: 'layer-type',
    readonly: true,
  },
]
const DATE_PROPERTIES: CreditProperty[] = [
  {
    key: 'effective_date',
    label: PropertyLabel.EffectiveDate,
    lens: 'transaction',
    type: 'date',
  },
  {
    key: 'maturity_date',
    label: PropertyLabel.MaturityDate,
    lens: 'transaction',
    type: 'date',
  },
  {
    key: 'term',
    label: PropertyLabel.Term,
    lens: 'transaction',
    type: 'text',
    readonly: true,
  },
  {
    key: 'call_date',
    label: PropertyLabel.CallDate,
    lens: 'transaction',
    type: 'date',
  },
  {
    key: 'call_type_selector',
    label: PropertyLabel.CallType,
    lens: 'transaction',
    type: 'switch',
    references: CALL_TYPE_SELECTOR_TYPES,
  },
]
/** GSE-only property, spliced in conditionally by CreditPropertiesComponent — do NOT add to
 * DATE_PROPERTIES, DEFAULT_XOL_ILN_PROPERTIES, or DEFAULT_QS_PROPERTIES since those are shared with MI. */
export const ACQUISITION_DATE_PROPERTY: CreditProperty = {
  key: 'acquisition_date',
  label: PropertyLabel.AcquisitionDate,
  lens: 'transaction',
  type: 'date',
}
/** GSE-only reference list for restricting the Limit Stepdown Type dropdown — applied conditionally
 * by CreditPropertiesComponent via getPropertyReferences(), never merged into the shared property list. */
export const GSE_LIMIT_STEPDOWN_REFERENCES: CreditPropertyReference[] = [
  { value: 'DETACH_SCHEDULE', viewValue: 'Detachment Schedule' },
  { value: 'UPB', viewValue: 'UPB' },
  { value: 'ERCF', viewValue: 'ERCF Capital' },
  { value: 'REFERENCE_TRANCHE', viewValue: 'Reference Tranche' },
]
/** GSE-only property, spliced in conditionally by CreditPropertiesComponent — do NOT add to
 * DATE_PROPERTIES, DEFAULT_XOL_ILN_PROPERTIES, or DEFAULT_QS_PROPERTIES since those are shared with MI. */
export const RETENTION_TYPE_PROPERTY: CreditProperty = {
  key: 'retention_type',
  label: PropertyLabel.RetentionType,
  lens: 'transaction',
  type: 'switch',
  references: [
    { value: 'fixed', viewValue: 'Fixed' },
    { value: 'floating', viewValue: 'Floating' },
  ],
}
/** GSE-only reference list for the Distressed Buckets dropdown — applied only when the
 * Reference Tranche panel is shown, never merged into the shared property list. */
export const DISTRESSED_BUCKETS_REFERENCES: CreditPropertyReference[] = [
  { value: '60plus', viewValue: '60+ Days' },
  { value: '90plus', viewValue: '90+ Days' },
]
/** GSE-only reference list for the CNL Gates Subordinate switch. cnl_gates_subordinate is a
 * real boolean on the backend; this property system has no boolean value type, so it's
 * represented as a 2-option string switch and converted at the read/write boundary
 * (see CreditPropertiesComponent's getPropertyValue/getTransactionUpdates). */
export const CNL_GATES_SUBORDINATE_REFERENCES: CreditPropertyReference[] = [
  { value: 'true', viewValue: 'Yes' },
  { value: 'false', viewValue: 'No' },
]
/** GSE-only properties for the Reference Tranche panel, spliced in conditionally by
 * CreditPropertiesComponent when limit_stepdown_type === 'REFERENCE_TRANCHE' — do NOT add to
 * DATE_PROPERTIES, DEFAULT_XOL_ILN_PROPERTIES, or DEFAULT_QS_PROPERTIES since those are shared with MI. */
export const REFERENCE_TRANCHE_PROPERTIES: CreditProperty[] = [
  {
    key: 'reference_tranche.min_ce_target',
    label: PropertyLabel.MinCeTarget,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'reference_tranche.delinquency_threshold_pct',
    label: PropertyLabel.DelinquencyThreshold,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'reference_tranche.distressed_buckets',
    label: PropertyLabel.DistressedBuckets,
    lens: 'transaction',
    type: 'dropdown',
    references: DISTRESSED_BUCKETS_REFERENCES,
  },
  {
    key: 'reference_tranche.cnl_gates_subordinate',
    label: PropertyLabel.CnlGatesSubordinate,
    lens: 'transaction',
    type: 'switch',
    references: CNL_GATES_SUBORDINATE_REFERENCES,
  },
  {
    key: 'reference_tranche.a1_cnl_threshold',
    label: PropertyLabel.A1CnlThreshold,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'reference_tranche.supplemental_target_pct',
    label: PropertyLabel.SupplementalTarget,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'reference_tranche.first_payment_date',
    label: PropertyLabel.FirstPaymentDate,
    lens: 'transaction',
    type: 'date',
  },
]
/** GSE-only property, spliced in alongside REFERENCE_TRANCHE_PROPERTIES — opens the existing
 * EditTriggerDialogComponent, reading/writing reference_tranche.cumulative_net_loss_schedule
 * instead of triggers.net_loss.threshold_data. Rendered via the existing 'popup' ngSwitchCase. */
export const CUMULATIVE_NET_LOSS_REFERENCE_TRANCHE_PROPERTY: CreditProperty = {
  key: 'reference_tranche.cumulative_net_loss_schedule',
  label: PropertyLabel.CumulativeNetLoss,
  lens: 'transaction',
  type: 'popup',
}
const DEFAULT_XOL_ILN_PROPERTIES: CreditProperty[] = [
  ...SHARED_PROPERTIES,
  {
    key: 'name',
    label: PropertyLabel.Name,
    lens: 'transaction',
    type: 'text',
  },
  {
    key: 'tranches',
    label: PropertyLabel.Tranches,
    lens: 'transaction',
    type: 'popup',
  },
  ...DATE_PROPERTIES,
  {
    key: 'limit_stepdown_type',
    label: PropertyLabel.LimitStepdownType,
    lens: 'transaction',
    type: 'dropdown',
    references: [
      { value: 'PMIERS', viewValue: 'PMIERS' },
      { value: 'RIF', viewValue: 'RIF' },
    ],
  },
  {
    key: 'triggers',
    label: PropertyLabel.StepDownTrigger,
    lens: 'transaction',
    type: 'stepdown-trigger',
  },
  {
    key: 'clean_up_call',
    label: PropertyLabel.CleanUpCall,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'pricingCurve',
    label: PropertyLabel.PricingCurve,
    lens: 'transaction',
    type: 'dropdown',
    references: [], // needs to be passed dynamically based on available pricing curves
  },
  {
    key: 'payment_frequency',
    label: PropertyLabel.PaymentFrequency,
    lens: 'transaction',
    type: 'switch',
    references: [
      { value: 'monthly', viewValue: 'Monthly' },
      { value: 'quarterly', viewValue: 'Quarterly' },
    ],
  },
  {
    key: 'fixed_cost',
    label: PropertyLabel.FixedCost,
    lens: 'transaction',
    type: 'currency',
  },
]
const DEFAULT_QS_PROPERTIES: CreditProperty[] = [
  ...SHARED_PROPERTIES,
  {
    key: 'name',
    label: PropertyLabel.Name,
    lens: 'transaction&tranche',
    type: 'text',
  },
  {
    key: 'color',
    label: PropertyLabel.Color,
    lens: 'tranche',
    type: 'color-picker',
  },
  {
    key: 'cession',
    label: PropertyLabel.Cession,
    lens: 'transaction&tranche',
    type: 'percentage',
  },
  {
    key: 'annual_lr_cap',
    label: PropertyLabel.AnnualLrCap,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'aggregate_lr_cap',
    label: PropertyLabel.AggregateLrCap,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'profit_commission',
    label: PropertyLabel.ProfitCommission,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'ceding_commission',
    label: PropertyLabel.CedingCommission,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'wacl',
    label: PropertyLabel.Wacl,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'wahc',
    label: PropertyLabel.Wahc,
    lens: 'transaction',
    type: 'percentage',
  },
  ...DATE_PROPERTIES,
  {
    key: 'payment_frequency',
    label: PropertyLabel.PaymentFrequency,
    lens: 'transaction',
    type: 'switch',
    references: [
      { value: 'monthly', viewValue: 'Monthly' },
      { value: 'quarterly', viewValue: 'Quarterly' },
    ],
  },
  {
    key: 'fixed_cost',
    label: PropertyLabel.FixedCost,
    lens: 'transaction',
    type: 'currency',
  },
]
export const CREDIT_TRANSACTION_TYPE_PROPERTIES_MAP: Record<
  CreditTransactionType,
  CreditProperty[]
> = {
  pmi_agg_xl: [...DEFAULT_XOL_ILN_PROPERTIES],
  pmi_qs: [...DEFAULT_QS_PROPERTIES],
  pmi_iln: [...DEFAULT_XOL_ILN_PROPERTIES],
  gse_agg_xl: [...DEFAULT_XOL_ILN_PROPERTIES],
  cm_xol: [...DEFAULT_XOL_ILN_PROPERTIES],
}

/* Term Sheet Panel */
const DEFAULT_XOL_ILN_TERMS: CreditProperty[] = [
  {
    key: 'businessCovered',
    label: PropertyLabel.BusinessCovered,
    lens: 'portfolio',
    type: 'date-range',
    readonly: true,
  },
  {
    key: 'effective_date',
    label: PropertyLabel.EffectiveDate,
    lens: 'transaction',
    type: 'date',
    readonly: true,
  },
  {
    key: 'maturity_date',
    label: PropertyLabel.MaturityDate,
    lens: 'transaction',
    type: 'date',
    readonly: true,
  },
  {
    key: 'term',
    label: PropertyLabel.Term,
    lens: 'transaction',
    type: 'text',
    readonly: true,
  },
  {
    key: 'call_date',
    label: PropertyLabel.CallDate,
    lens: 'transaction',
    type: 'date',
    readonly: true,
  },
  {
    key: 'limit_stepdown_type',
    label: PropertyLabel.LimitStepdownType,
    lens: 'transaction',
    type: 'text-upper',
    readonly: true,
  },
  {
    key: 'clean_up_call',
    label: PropertyLabel.CleanUpCall,
    lens: 'transaction',
    type: 'percentage',
    readonly: true,
  },
  {
    key: 'attach',
    label: PropertyLabel.Attach,
    lens: 'tranche',
    type: 'percentage',
    readonly: true,
  },
  {
    key: 'detach',
    label: PropertyLabel.Detach,
    lens: 'tranche',
    type: 'percentage',
    readonly: true,
  },
  {
    key: 'cession',
    label: PropertyLabel.Cession,
    lens: 'tranche',
    type: 'percentage',
    readonly: true,
  },
  {
    key: 'expectedPremium',
    label: PropertyLabel.ExpectedPremium,
    lens: 'tranche',
    type: 'currency',
    readonly: true,
  },
  {
    key: 'reinsurerParticipationLimit',
    label: PropertyLabel.ReinsurerParticipationLimit,
    lens: 'tranche',
    type: 'currency',
    readonly: true,
  },
  {
    key: 'reinsurer_participation_percent',
    label: PropertyLabel.ReinsurerParticipationPercent,
    lens: 'tranche',
    type: 'percentage',
  },
  {
    key: 'rol',
    label: PropertyLabel.ROL,
    lens: 'tranche',
    type: 'percentage',
  },
  {
    key: 'collateral_percent',
    label: PropertyLabel.CollateralRequirement,
    lens: 'structure',
    type: 'percentage',
  },
  {
    key: 'rorac_scenario',
    label: PropertyLabel.RORACScenario,
    lens: 'structure',
    type: 'dropdown-tree',
    fields: { groupBy: 'model_name', text: 'scenario_name', value: 'id' },
    dataSource: [], // needs to be passed dynamically based on available scenarios
    dataSourceSort: sortModelScenarioMapping,
  },
]

const DEFAULT_QS_TERMS: CreditProperty[] = [
  {
    key: 'businessCovered',
    label: PropertyLabel.BusinessCovered,
    lens: 'portfolio',
    type: 'date-range',
    readonly: true,
  },
  {
    key: 'effective_date',
    label: PropertyLabel.EffectiveDate,
    lens: 'transaction',
    type: 'date',
    readonly: true,
  },
  {
    key: 'maturity_date',
    label: PropertyLabel.MaturityDate,
    lens: 'transaction',
    type: 'date',
    readonly: true,
  },
  {
    key: 'term',
    label: PropertyLabel.Term,
    lens: 'transaction',
    type: 'text',
    readonly: true,
  },
  {
    key: 'call_date',
    label: PropertyLabel.CallDate,
    lens: 'transaction',
    type: 'date',
    readonly: true,
  },
  {
    key: 'annual_lr_cap',
    label: PropertyLabel.AnnualLrCap,
    lens: 'transaction',
    type: 'percentage',
    readonly: true,
  },
  {
    key: 'aggregate_lr_cap',
    label: PropertyLabel.AggregateLrCap,
    lens: 'transaction',
    type: 'percentage',
    readonly: true,
  },
  {
    key: 'cession',
    label: PropertyLabel.Cession,
    lens: 'tranche',
    type: 'percentage',
    readonly: true,
  },
  {
    key: 'expectedPremium',
    label: PropertyLabel.ExpectedPremium,
    lens: 'tranche',
    type: 'currency',
    readonly: true,
  },
  {
    key: 'reinsurerParticipationLimit',
    label: PropertyLabel.ReinsurerParticipationLimit,
    lens: 'tranche',
    type: 'currency',
    readonly: true,
  },
  {
    key: 'reinsurer_participation_percent',
    label: PropertyLabel.ReinsurerParticipationPercent,
    lens: 'tranche',
    type: 'percentage',
  },
  {
    key: 'margin',
    label: PropertyLabel.Margin,
    lens: 'transaction',
    type: 'percentage',
  },
  {
    key: 'profit_commission',
    label: PropertyLabel.ProfitCommission,
    lens: 'transaction',
    type: 'percentage',
    readonly: true,
  },
  {
    key: 'ceding_commission',
    label: PropertyLabel.CedingCommission,
    lens: 'transaction',
    type: 'dropdown',
    dataSource: CEDING_COMMISSION_OPTIONS,
  },
  {
    key: 'rorac_scenario',
    label: PropertyLabel.RORACScenario,
    lens: 'structure',
    type: 'dropdown-tree',
    fields: { groupBy: 'model_name', text: 'scenario_name', value: 'id' },
    dataSource: [], // needs to be passed dynamically based on available scenarios
    dataSourceSort: sortModelScenarioMapping,
  },
]
/** GSE-only term list for XOL/ILN — do NOT merge into DEFAULT_XOL_ILN_TERMS since that array is
 * shared with MI. Selected conditionally by CreditTermSheetService.buildTermSheet() based on
 * portfolio.data_type. */
export const GSE_XOL_TERMS: CreditProperty[] = [
  ...DEFAULT_XOL_ILN_TERMS,
  {
    key: 'dollarRetention',
    label: PropertyLabel.DollarRetention,
    lens: 'tranche',
    type: 'currency',
    readonly: true,
  },
  {
    key: 'dollarLimit',
    label: PropertyLabel.DollarLimit,
    lens: 'tranche',
    type: 'currency',
    readonly: true,
  },
]

/** GSE-only term list for Quota Share — do NOT merge into DEFAULT_QS_TERMS since that array is
 * shared with MI Quota Share (both use transaction_type 'pmi_qs'). Selected conditionally by
 * CreditTermSheetService.buildTermSheet() based on portfolio.data_type. */
export const GSE_QS_TERMS: CreditProperty[] = [
  ...DEFAULT_QS_TERMS,
  {
    key: 'dollarRetention',
    label: PropertyLabel.DollarRetention,
    lens: 'tranche',
    type: 'currency',
    readonly: true,
  },
  {
    key: 'dollarLimit',
    label: PropertyLabel.DollarLimit,
    lens: 'tranche',
    type: 'currency',
    readonly: true,
  },
]

export const CREDIT_TRANSACTION_TYPE_TERMS_MAP: Record<CreditTransactionType, CreditProperty[]> = {
  pmi_agg_xl: [...DEFAULT_XOL_ILN_TERMS],
  pmi_qs: [...DEFAULT_QS_TERMS],
  pmi_iln: [...DEFAULT_XOL_ILN_TERMS],
  gse_agg_xl: [...DEFAULT_XOL_ILN_TERMS],
  cm_xol: [...DEFAULT_XOL_ILN_TERMS],
}

export const USER_TRANCHE_TERMS: Set<CreditPropertyKey> = new Set([
  'collateral_percent', // structure
  'rorac_scenario', // structure
  'ceding_commission', // transaction, QS only
  'profit_commission', // transaction, QS only
  'margin', // transaction, QS only
  'rol', // tranche, XOL only
  'reinsurer_participation_percent', // tranche
])

export type CreditTermSheet = {
  trancheTerms: Record<string, CreditProperty[]>
  otherTerms: CreditProperty[]
}
